
import {
    PropType,
    computed, defineComponent,
} from 'vue';
import { toWritableComputedProps } from 'src/util/composables/writable-computed-prop';
// import { messages } from 'src/i18n/i18n';
import {
    Encoding,
    SnfTokenDatetime,
} from '@redviking/argonaut-util/src/serial-numbers';
import { useEditing } from 'src/components/EntityDetail';
import { TimezoneSelector } from 'src/components/TimezoneSelector';

const formatItems: {
    [type in SnfTokenDatetime['format']]: {
        title: string;
        desc: string;
    };
} = {
    year4: {
        title: 'Year 4',
        desc: 'Full year. Ex: 1999',
    },
    year2: {
        title: 'Year 2',
        desc: 'Last two digits of year. Ex: 99',
    },
    year1: {
        title: 'Year 1',
        desc: 'Last digit of year. Ex: 9',
    },
    quarter: {
        title: 'Quarter',
        desc: 'Quarter of the year 1-4',
    },
    month: {
        title: 'Month',
        desc: '1-12 Starting with January',
    },
    julian: {
        title: 'Julian Day',
        desc: 'Day of the year taking into account leap years. Ex: March 1st = 60 or 61',
    },
    modifiedJulianDay: {
        title: 'Modified Julian Day',
        desc: 'Day of the year always accounting for leap day. Ex: March 1st = 61',
    },
    monthDay: {
        title: 'Month Day',
        desc: '1-31 Starting with the first day of the month',
    },
    weekDayMonday: {
        title: 'Week Day (Monday)',
        desc: '1-7 Starting with Monday',
    },
    weekDay: {
        title: 'Week Day',
        desc: '1-7 Starting with Sunday',
    },
    hour: {
        title: 'Hour',
        desc: '0-23 Starting with midnight',
    },
    minute: {
        title: 'Minute',
        desc: '0-59 Starting with the top of the hour',
    },
    second: {
        title: 'Second',
        desc: '0-59 Starting with the top of the minute',
    },
};

export const formatItemsMap = Object.entries(formatItems).map(([ value, { title, desc } ]) => ({ value, title, desc }));

const encodingItems: {
    [type in Encoding]: {
        title: string;
        desc: string;
    };
} = {
    alphabetics: {
        title: 'Alphabetics',
        desc: 'Alphabetic characters (A-Z)',
    },
    alphanumerics: {
        title: 'Alphanumerics',
        desc: 'Alphanumeric characters (A-Z, 0-9)',
    },
    numerics: {
        title: 'Numerics',
        desc: 'Numeric characters (0-9)',
    },
    safeAlphabetics: {
        title: 'Safe Alphabetics',
        desc: 'Safe alphabetic characters (A-Z, no similar looking characters i.e. I, L, O, S)',
    },
    safeAlphanumerics: {
        title: 'Safe Alphanumerics',
        desc: 'Safe alphanumeric characters (A-Z, 0-9, no similar looking characters i.e. I, L, O, S)',
    },
};

export const encodingItemsMap = Object.entries(encodingItems).map(([ value, { title, desc } ]) => ({ value, title, desc }));

export default defineComponent({
    name: 'DatetimeToken',
    components: {
        TimezoneSelector,
    },
    props: {
        readonly: { type: Boolean, required: true },
        value: { type: Object as PropType<SnfTokenDatetime>, required: true },
    },
    setup (props, { emit }) {
        const editing = useEditing.useInject().ref;
        const token = computed({
            get: () => props.value,
            set: v => emit('input', v),
        });

        const {
            format,
            timezone,
            encoding,
        } = toWritableComputedProps(token);

        return {
            token,
            format,
            editing,
            timezone,
            encoding,
            formatItemsMap,
            encodingItemsMap,
        };
    },
});
